import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "../lib/api.js";
import { Plus, ShieldAlert, X } from "lucide-react";

const STATUS_COLORS = {
  // PT (current)
  "Incompleto": "bg-[#E2E8F0] text-[#495057]",
  "Evidências em recolha": "bg-[#FFF3CD] text-[#856404]",
  "Submetido à marca": "bg-[#E0E7FF] text-[#1E3A8A]",
  "Aguarda resposta da marca": "bg-[#FFF3CD] text-[#856404]",
  "Informação adicional solicitada": "bg-[#FFE4B5] text-[#7C5400]",
  "Aprovado": "bg-[#D4EDDA] text-[#155724]",
  "Não aprovado": "bg-[#F8D7DA] text-[#721C24]",
  "Reparação autorizada": "bg-[#CCE5FF] text-[#004085]",
  "Reparação concluída": "bg-[#B8DAFF] text-[#004085]",
  "Faturado": "bg-[#D1ECF1] text-[#0C5460]",
  "Encerrado": "bg-[#E2E8F0] text-[#495057]",
  "Cancelado": "bg-[#F8D7DA] text-[#721C24]",
  // Legacy EN (defensive — should be migrated by backend _clean)
  Draft: "bg-[#E2E8F0] text-[#495057]",
  "Submitted to brand": "bg-[#E0E7FF] text-[#1E3A8A]",
  "Pending brand response": "bg-[#FFF3CD] text-[#856404]",
  Approved: "bg-[#D4EDDA] text-[#155724]",
  Rejected: "bg-[#F8D7DA] text-[#721C24]",
  Invoiced: "bg-[#D1ECF1] text-[#0C5460]",
  Closed: "bg-[#E2E8F0] text-[#495057]",
  Cancelled: "bg-[#F8D7DA] text-[#721C24]",
};

export default function WarrantyClaimsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [v2enums, setV2enums] = useState(null);
  const [cases, setCases] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  // When opened from a SOM "Criar Garantia" placeholder, the Create dialog auto-opens with the SOM pre-selected.
  const [preselectSomId, setPreselectSomId] = useState(null);

  const load = async () => {
    const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
    const { data } = await api.get(`/warranty-claims${q}`);
    setItems(data);
    return data;
  };

  useEffect(() => {
    api.get("/v2/enums").then((r) => setV2enums(r.data));
    api.get("/service-cases?open_only=false").then((r) => setCases(r.data));
  }, []);
  useEffect(() => { load(); }, [statusFilter]);

  // Auto-open the Create dialog when navigated from the SOM workflow placeholder
  // (URL pattern: /warranty-claims?create=1&service_case_id=<id>).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("create") === "1") {
      const sid = params.get("service_case_id");
      setShowCreate(true);
      if (sid) setPreselectSomId(sid);
      params.delete("create");
      params.delete("service_case_id");
      const next = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${next ? `?${next}` : ""}`);
    }
  }, []);

  // ?focus=<id> now redirects to the full-page detail (iter 49).
  // ?from=som is propagated via router state so the detail page's back
  // button returns to the SOM without a URL query.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focus");
    const from = params.get("from");
    if (!focusId) return;
    const target = items.find((i) => i.id === focusId);
    if (target) {
      const state = from === "som" && target.service_case_id
        ? { fromSomId: target.service_case_id }
        : undefined;
      navigate(`/warranty-claims/${focusId}`, { replace: true, state });
    }
  }, [items, navigate]);

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="warranty-page">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-[#1F3864]" />
            Garantias
          </h1>
          <p className="text-xs text-[#6C757D]">Submissão de reclamações às marcas OEM com evidências, acompanhamento de resposta e facturação.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-[#1F3864] hover:bg-[#142542] text-white px-3 py-1.5 rounded-sm text-sm flex items-center gap-1"
          data-testid="new-warranty-btn"
        >
          <Plus className="w-3 h-3" /> Nova garantia
        </button>
      </div>

      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <label className="text-xs text-[#6C757D] uppercase tracking-widest">Estado</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1 border border-gray-300 rounded-sm text-sm"
          data-testid="wc-status-filter"
        >
          <option value="">Todos</option>
          {v2enums?.warranty_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="ml-auto text-xs text-[#6C757D]">{items.length} garantia{items.length === 1 ? "" : "s"}</div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
            <tr>
              <th className="px-3 py-2 text-left">Nº</th>
              <th className="px-3 py-2 text-left">Obra</th>
              <th className="px-3 py-2 text-left">Cliente / Veículo</th>
              <th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-left">Marca</th>
              <th className="px-3 py-2 text-right">Estimado</th>
              <th className="px-3 py-2 text-right">Aprovado</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-right">Abrir</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="9" className="px-3 py-4 text-center text-xs text-[#6C757D]">Sem garantias registadas.</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className="border-t border-gray-100 hover:bg-[#F8FAFC] cursor-pointer" onClick={() => navigate(`/warranty-claims/${it.id}`)} data-testid={`wc-row-${it.id}`}>
                <td className="px-3 py-2 font-mono text-[#1F3864]">{it.number}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  {it.service_case_id ? (
                    <Link to={`/service-cases/${it.service_case_id}`} onClick={(e) => e.stopPropagation()} className="text-[#1F3864] hover:underline" data-testid={`wc-som-link-${it.id}`}>
                      {it.service_case_number}
                    </Link>
                  ) : "—"}
                </td>
                <td className="px-3 py-2">{it.customer_name} <span className="text-[#6C757D] font-mono text-xs">· {it.vehicle_registration}</span></td>
                <td className="px-3 py-2 text-xs">{it.claim_type}</td>
                <td className="px-3 py-2 text-xs">{it.brand}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{Number(it.estimated_amount || 0).toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{Number(it.approved_amount || 0).toLocaleString()}</td>
                <td className="px-3 py-2"><StatusBadge status={it.status} /></td>
                <td className="px-3 py-2 text-right text-xs"><button className="text-[#1F3864] hover:underline" data-testid={`wc-open-${it.id}`}>Abrir</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && v2enums && (
        <CreateDialog v2enums={v2enums} cases={cases} preselectSomId={preselectSomId} onClose={() => { setShowCreate(false); setPreselectSomId(null); }} onSaved={() => { setShowCreate(false); setPreselectSomId(null); load(); }} />
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  return <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${STATUS_COLORS[status] || "bg-[#E2E8F0] text-[#495057]"}`}>{status}</span>;
}

function CreateDialog({ v2enums, cases, preselectSomId, onClose, onSaved }) {
  const [form, setForm] = useState({
    service_case_id: preselectSomId || cases[0]?.id || "",
    claim_type: v2enums.warranty_claim_types[0],
    description: "",
    estimated_amount: 0,
    brand_submission_ref: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/warranty-claims", { ...form, estimated_amount: Number(form.estimated_amount) || 0 });
      onSaved();
    } catch (e) {
      setErr(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-xl" onClick={(e) => e.stopPropagation()} data-testid="wc-create-dialog">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-heading text-base font-bold">Nova garantia</h2>
          <button onClick={onClose} className="text-[#6C757D] hover:text-[#1A1A2E]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Obra (SOM) *">
            <select value={form.service_case_id} onChange={(e) => setForm({ ...form, service_case_id: e.target.value })} className={inputCls} data-testid="wc-case-select">
              <option value="">Seleccione…</option>
              {cases.map((c) => <option key={c.id} value={c.id}>{c.number} · {c.customer_name} · {c.brand} {c.vehicle_registration}</option>)}
            </select>
          </Field>
          <Field label="Tipo de reclamação *">
            <select value={form.claim_type} onChange={(e) => setForm({ ...form, claim_type: e.target.value })} className={inputCls}>
              {v2enums.warranty_claim_types.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Descrição *">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className={inputCls} data-testid="wc-description" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Valor estimado (MZN)">
              <input type="number" value={form.estimated_amount} onChange={(e) => setForm({ ...form, estimated_amount: e.target.value })} className={inputCls} data-testid="wc-estimated" />
            </Field>
            <Field label="Referência submissão">
              <input value={form.brand_submission_ref} onChange={(e) => setForm({ ...form, brand_submission_ref: e.target.value })} className={inputCls} placeholder="opcional em rascunho" />
            </Field>
          </div>
          {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="wc-error">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]">Cancelar</button>
          <button onClick={submit} disabled={busy || !form.service_case_id || !form.description.trim()} className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-60" data-testid="wc-submit">
            {busy ? "A gravar…" : "Criar garantia"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      {children}
    </label>
  );
}

